/**
 * Bi-directional sticky for the product info column.
 *
 * Four-state machine:
 *   STUCK_TOP  — position: sticky, top: topPad      (card pinned below header)
 *   FREE       — position: relative, top: offset     (card scrolls with page)
 *   STUCK_BOT  — position: sticky, top: bottomStick  (card bottom pinned)
 *
 * STUCK states use native sticky (smooth, browser-driven).
 * FREE state uses relative positioning (no sticky recalculation,
 * immune to layout shifts, consistent visual behavior).
 *
 * Transitions:
 *   STUCK_TOP  → scroll down >5px  → FREE (relative at current offset)
 *   FREE       → card top hits hdr → STUCK_TOP (sticky)
 *   FREE       → card bot hits btm → STUCK_BOT (sticky)
 *   STUCK_BOT  → scroll up >5px    → FREE (relative at current offset)
 */
(function () {
  'use strict';

  var BOTTOM_PAD = 24;
  var HEADER_GAP = 16;
  var DEAD_ZONE  = 5;
  var DESKTOP_BP = 750;
  var THRESHOLD  = 5;

  function init() {
    var stickyEl = document.querySelector('.product__info-container');
    var wrapper  = document.querySelector('.product__info-wrapper');
    var scrollEl = document.querySelector('.product__media-wrapper');
    if (!stickyEl || !wrapper || !scrollEl) return;
    if (window.innerWidth < DESKTOP_BP) return;

    var headerEl = document.querySelector('sticky-header');

    var state       = 'simple';
    var lastScrollY = window.scrollY;

    function getFullHeaderH() {
      if (!headerEl) return 0;
      var inner = headerEl.querySelector('header');
      var secondary = headerEl.querySelector('.header-secondary__wrapper');
      var h = 0;
      if (inner) h += inner.offsetHeight;
      if (secondary) h += Math.max(secondary.offsetHeight, secondary.scrollHeight);
      return h || headerEl.offsetHeight;
    }

    function getTopPad() {
      return getFullHeaderH() + HEADER_GAP;
    }

    function getBottomStick() {
      return window.innerHeight - BOTTOM_PAD - stickyEl.offsetHeight;
    }

    function toRelative() {
      var cardRect    = stickyEl.getBoundingClientRect();
      var wrapperRect = wrapper.getBoundingClientRect();
      var offset      = cardRect.top - wrapperRect.top;
      stickyEl.style.position = 'relative';
      stickyEl.style.top = offset + 'px';
    }

    function ensureMinHeight() {
      var stickyH   = stickyEl.offsetHeight;
      var viewportH = window.innerHeight;
      var topPad    = getTopPad();
      var scrollH   = scrollEl.offsetHeight;

      // The wrapper needs exactly enough height for the card to travel
      // from stuck-top to stuck-bottom. The travel distance is the
      // amount the card exceeds the visible area between header and
      // viewport bottom.
      var available = viewportH - topPad - BOTTOM_PAD;
      var overflow  = stickyH - available;
      var needed    = stickyH + overflow; // = 2*stickyH - available

      // Never shrink below the left column height
      wrapper.style.minHeight = (scrollH > needed ? scrollH : needed) + 'px';
    }

    function setup() {
      var stickyH   = stickyEl.offsetHeight;
      var viewportH = window.innerHeight;
      var topPad    = getTopPad();
      var available = viewportH - topPad - BOTTOM_PAD;

      if (stickyH === 0 || stickyH <= available) {
        state = 'simple';
        wrapper.style.minHeight = '';
        stickyEl.style.position = 'sticky';
        stickyEl.style.top = topPad + 'px';
        return;
      }

      ensureMinHeight();

      // Determine correct state from current visual position
      var rect = stickyEl.getBoundingClientRect();
      var bottomTarget = viewportH - BOTTOM_PAD;

      if (rect.top >= topPad - THRESHOLD) {
        state = 'stuck-top';
        stickyEl.style.position = 'sticky';
        stickyEl.style.top = topPad + 'px';
      } else if (rect.bottom <= bottomTarget + THRESHOLD) {
        state = 'stuck-bottom';
        stickyEl.style.position = 'sticky';
        stickyEl.style.top = getBottomStick() + 'px';
      } else {
        state = 'free';
        toRelative();
      }

      lastScrollY = window.scrollY;
    }

    function onScroll() {
      var scrollY = window.scrollY;
      var delta   = scrollY - lastScrollY;
      lastScrollY = scrollY;

      if (state === 'simple') return;

      var topPad    = getTopPad();
      var viewportH = window.innerHeight;

      // ── STUCK TOP ──
      if (state === 'stuck-top') {
        // Keep in sync with header height changes
        stickyEl.style.top = topPad + 'px';
        if (delta > DEAD_ZONE) {
          toRelative();
          state = 'free';
        }
        return;
      }

      // ── STUCK BOTTOM ──
      if (state === 'stuck-bottom') {
        if (delta < -DEAD_ZONE) {
          toRelative();
          state = 'free';
        }
        return;
      }

      // ── FREE ──
      if (state === 'free') {
        var rect = stickyEl.getBoundingClientRect();
        var bottomTarget = viewportH - BOTTOM_PAD;

        // Card top reached header → stick at top
        if (rect.top >= topPad - THRESHOLD) {
          state = 'stuck-top';
          stickyEl.style.position = 'sticky';
          stickyEl.style.top = topPad + 'px';
          return;
        }

        // Card bottom reached viewport bottom → stick at bottom
        if (rect.bottom <= bottomTarget + THRESHOLD) {
          state = 'stuck-bottom';
          stickyEl.style.position = 'sticky';
          stickyEl.style.top = getBottomStick() + 'px';
          return;
        }

        // Between boundaries — do nothing, card scrolls naturally
      }
    }

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        if (window.innerWidth < DESKTOP_BP) {
          stickyEl.style.position = '';
          stickyEl.style.top = '';
          wrapper.style.minHeight = '';
          state = 'simple';
          return;
        }
        setup();
      }, 250);
    });

    // Batch scroll handling to one execution per animation frame.
    // Prevents multiple DOM reads per frame for smoother compositing.
    var scrollRAF = null;
    window.addEventListener('scroll', function () {
      if (!scrollRAF) {
        scrollRAF = requestAnimationFrame(function () {
          scrollRAF = null;
          onScroll();
        });
      }
    }, { passive: true });

    setup();
  }

  function start() {
    setTimeout(init, 750);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
